import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BatteryCharging, Cpu, Flame, Sun, Tractor } from "lucide-react";

const SCENARIOS = {
  normal: {
    label: "Normal Operation",
    description:
      "Solar and wind generation supply the farm load directly. The energy management layer routes any surplus to the battery for fast stabilization, then on to hydrogen conversion — banking long-duration reserve for later.",
  },
  outage: {
    label: "Extended Outage",
    description:
      "With generation low or the grid down, renewable input steps back. Stored energy carries the load: the battery absorbs sudden swings while hydrogen conversion sustains critical farm systems for the duration of the event.",
  },
};

const PATHS = [
  { id: "generation-management", d: "M 155 210 L 345 210" },
  { id: "management-battery", d: "M 405 210 C 485 210 500 118 590 118" },
  { id: "management-hydrogen", d: "M 405 210 C 485 210 500 302 590 302" },
  { id: "battery-load", d: "M 660 118 C 755 118 760 210 845 210" },
  { id: "hydrogen-load", d: "M 660 302 C 755 302 760 210 845 210" },
];

const pathState = (id, scenario) => {
  if (scenario === "normal") return "primary";
  if (id === "hydrogen-load") return "reserve";
  if (id === "battery-load") return "secondary";
  return "inactive";
};

const FlowPath = ({ path, scenario, index }) => {
  const state = pathState(path.id, scenario);
  const stroke = state === "reserve" ? "#1C3B2B" : state === "primary" ? "#1E535C" : "#AEB8BA";
  const active = state !== "inactive";

  return (
    <g>
      <path d={path.d} fill="none" stroke="#D7DEDC" strokeWidth="1" />
      <motion.path
        key={`${scenario}-${path.id}`}
        d={path.d}
        fill="none"
        stroke={stroke}
        strokeWidth={state === "reserve" ? 2.5 : active ? 1.75 : 1}
        strokeDasharray={active ? "2 12" : "3 12"}
        strokeLinecap="round"
        markerEnd={`url(#arrow-${state})`}
        initial={{ opacity: 0, pathLength: 0.65 }}
        animate={{ opacity: active ? 0.9 : 0.22, pathLength: 1, strokeDashoffset: active ? [0, -42] : 0 }}
        transition={{
          opacity: { duration: 0.45 },
          pathLength: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
          strokeDashoffset: { duration: 2.4, repeat: Infinity, ease: "linear", delay: index * 0.08 },
        }}
      />
    </g>
  );
};

const FlowNode = ({ id, icon: Icon, label, detail, duration, state = "active" }) => (
  <motion.div
    data-testid={`diagram-node-${id}`}
    animate={{ opacity: state === "subdued" ? 0.38 : state === "secondary" ? 0.68 : 1, scale: state === "primary" ? 1.015 : 1 }}
    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
    className="relative z-10 flex min-w-0 flex-col items-center text-center"
  >
    <div className={`flex h-14 w-14 items-center justify-center rounded-full border bg-white transition-[border-color,color,box-shadow] duration-500 ${state === "primary" ? "border-forest text-forest shadow-[0_10px_28px_-20px_rgba(28,59,43,0.65)]" : state === "subdued" ? "border-borderLight text-inkMuted" : "border-teal/30 text-teal"}`}>
      <Icon size={20} strokeWidth={1.7} />
    </div>
    <h3 className="mt-4 font-heading text-base font-semibold text-ink">{label}</h3>
    <p className="mt-1 text-xs leading-relaxed text-inkMuted">{detail}</p>
    {duration && (
      <span className="mt-2 border-b border-teal/30 pb-1 font-mono text-[11px] text-teal">
        {duration}
      </span>
    )}
  </motion.div>
);

const MobileConnector = ({ active = true }) => (
  <div className="relative mx-auto h-12 w-px overflow-hidden bg-borderSubtle lg:hidden">
    {active && (
      <motion.span
        className="absolute left-[-2px] top-0 h-1.5 w-1.5 rounded-full bg-teal"
        animate={{ y: [0, 42] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
      />
    )}
  </div>
);

export const EnergyDiagram = () => {
  const [scenario, setScenario] = useState("normal");
  const outage = scenario === "outage";

  return (
    <section id="microgrid-flow" data-testid="energy-diagram-section" className="bg-surfaceLight px-6 py-[30px] sm:py-[40px] lg:px-12 lg:py-[70px]">
      <div className="lg:mx-auto lg:max-w-[1280px]">
        <div className="mb-12 flex flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <h2 data-testid="energy-diagram-title" className="font-heading text-[30px] font-semibold leading-tight text-ink sm:text-[35px] lg:text-[41px]">
              Battery for power. Hydrogen
              <br className="hidden lg:block" /> for duration
            </h2>
            <p data-testid="energy-diagram-description" className="mt-4 text-[15px] leading-relaxed text-inkSoft">
              Battery storage responds quickly to short-duration load changes. Hydrogen provides
              <br className="hidden lg:block" /> an additional energy reserve when backup requirements extend from hours to days.
            </p>
          </div>
          <div className="flex w-fit gap-1.5 rounded-full border border-borderLight bg-white p-1.5" data-testid="diagram-scenario-toggle">
            {Object.entries(SCENARIOS).map(([key, value]) => (
              <button
                key={key}
                type="button"
                aria-pressed={scenario === key}
                data-testid={`diagram-toggle-${key}`}
                onClick={() => setScenario(key)}
                className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${scenario === key ? "bg-teal text-white" : "text-inkMuted hover:text-ink"}`}
              >
                {value.label}
              </button>
            ))}
          </div>
        </div>

        <div className="relative overflow-hidden border-y border-borderSubtle py-10 lg:min-h-[420px] lg:py-0" data-testid="energy-flow-diagram">
          <svg className="pointer-events-none absolute inset-0 hidden h-full w-full lg:block" viewBox="0 0 1000 420" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <marker id="arrow-primary" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="#1E535C" /></marker>
              <marker id="arrow-reserve" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="#1C3B2B" /></marker>
              <marker id="arrow-secondary" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="#AEB8BA" /></marker>
              <marker id="arrow-inactive" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="#AEB8BA" /></marker>
            </defs>
            {PATHS.map((path, index) => <FlowPath key={path.id} path={path} scenario={scenario} index={index} />)}
          </svg>

          <div className="relative grid grid-cols-1 items-center gap-0 lg:min-h-[420px] lg:grid-cols-4">
            <FlowNode id="gen" icon={Sun} label="Solar + Wind" detail="Renewable generation" state={outage ? "subdued" : "active"} />
            <MobileConnector active={!outage} />
            <FlowNode id="mgmt" icon={Cpu} label="Energy Management" detail="Routing and control" state={outage ? "subdued" : "active"} />
            <MobileConnector active={!outage} />
            <div className="grid grid-cols-2 gap-5 py-2 lg:grid-cols-1 lg:gap-14 lg:py-0">
              <FlowNode id="battery" icon={BatteryCharging} label="Battery" detail="Fast response" duration="Seconds → Hours" state={outage ? "secondary" : "active"} />
              <FlowNode id="hydrogen" icon={Flame} label="Hydrogen System" detail="Long-duration energy storage" duration="Hours → Days" state={outage ? "primary" : "active"} />
            </div>
            <MobileConnector />
            <FlowNode id="load" icon={Tractor} label="Farm Load" detail="Barns · Coolers · Pumps" state="active" />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.p
            key={scenario}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            data-testid="diagram-scenario-description"
            className="mt-8 max-w-2xl text-[15px] leading-relaxed text-inkSoft"
          >
            {SCENARIOS[scenario].description}
          </motion.p>
        </AnimatePresence>
      </div>
    </section>
  );
};